import { queryOptions } from '@tanstack/react-query'
import type { AskHave, AskQuestion } from './auditAnswers'
import { apiGet, apiPost } from './client'

/**
 * IT Audit, mutat din `application/controllers/settings/Audit.php`.
 *
 * Formele sunt cele pe care le desenau machetele din
 * `views/version-3/settings/audit/`: ce scrie `Services\Audit\Catalog`,
 * `Runs`, `Rating`, `Collectors`, `Probes`, `Depth` ajunge aici neschimbat, prin
 * `App\Audit\AuditServices`. `apiGet` pune EL `/api/v2` in fata caii.
 */

export interface AuditArea {
  code: string
  title: string
  mode: string
  auto: number
  scope: string
  sides: string[]
  extracts: string
  checks: string
  auditor: string
}

export interface AuditType {
  id: number
  title: string
  objective: string
  automation: number
  mix: number[]
  areas: AuditArea[]
}

export interface AuditSide {
  short: string
  about: string
  icon: string
}

export interface AuditLastRun {
  at: string
  status: string
}

/** `Rating::domain()` */
export interface AuditDomainRating {
  mean: number | null
  lowest: number
  rated: number
  flagged: string[]
  /* Din cate rulari e adunata media, si intre ce date. Nota pe arie e cea mai
     NOUA pe acea arie, deci media poate cuprinde Reteaua de saptamana asta si
     Stocarea de acum trei luni - amestecul ramane, dar se spune. */
  runs: number
  oldest: string
  newest: string
}

/** `Rating::overall()` */
export interface AuditOverall {
  mean: number | null
  serious: number
  why: string
}

export interface AuditIndex {
  types: AuditType[]
  counts: { types: number; areas: number }
  runCounts: Record<string, number>
  lastRuns: Record<string, AuditLastRun>
  ratings: Record<string, AuditDomainRating>
  overall: AuditOverall
  findings: Record<string, number> | null
  sides: Record<string, AuditSide>
  planWide: Record<string, number>
  crossMap: Record<string, string>
}

export interface AuditProbe {
  why: string
  linux: string
  windows: string
}

/** `Collectors::forArea()` + `Probes::forArea()` + `Coverage::forArea()` */
export interface AuditAreaPlan {
  tools: string[]
  asks: string[]
  measurable: boolean
  live: { check: string; command: string }[]
  probes: AuditProbe[]
  reach: { checks: number; decides: number; level: string }
}

export interface AuditRun {
  ID: number
  status: string
  company: string
  who: string
  area_code: string
  conversation_id: number
  created_at: string
  started_at: string
  finished_at: string
  error: string
  hasReport: boolean
  hasSolutions: boolean
}

export interface AuditPick {
  ID: number
  name: string
  n: number
}

export interface AuditDepth {
  label: string
  about: string
  cost: string
}

export interface AuditTypePage {
  type: AuditType
  types: AuditType[]
  sides: Record<string, AuditSide>
  plan: Record<string, AuditAreaPlan>
  measurable: number
  stepsReady: boolean
  mayRun: boolean
  offices: AuditPick[]
  deviceTypes: AuditPick[]
  depths: Record<string, AuditDepth>
  rated: AuditDomainRating
  tally: { pass: number; fail: number; unknown: number; observed: number }
  runs: AuditRun[]
  latest: AuditRun | null
  ready: boolean
  runCounts: Record<string, number>
  /* Cate rulari sunt CU TOTUL: `runs` de mai sus e taiata la zece ca sa se
     poata citi, si o cifra care se opreste din numarat odata cu lista ar
     contrazice banda de sus, care numara in baza. */
  runsAll: number
  questions: Record<string, AskQuestion[]>
  asked: Record<string, { have: Record<string, AskHave>; coverage: { asked: number; answered: number; stale: number } }>
  answersReady: boolean
  before: AuditBefore
  reportHtml: string
}

/** Tinta ultimei rulari, ca sa se poata pune inapoi in casute. */
export interface AuditBefore {
  sides: string[]
  classes: string[]
  offices: string[]
  types: string[]
  devices: string[]
  named: { id: number; name: string }[]
  depth: string
}

/** Un pas dat mai departe: ce se trimite si unde. */
export interface AuditHandOut {
  step_id: number
  area: string
  title: string
  question: string
}

export interface AuditStep {
  step_id: number
  area: string
  title: string
  status: string
}

export interface AuditQueueAnswer {
  ok: boolean
  error?: string
  run_id?: number
  conversation_id?: number
  brief?: string
  next?: AuditHandOut | null
  steps?: AuditStep[]
}

export interface AuditStatus extends AuditQueueAnswer {
  /** Serverul a raspuns, dar nu poate citi rularea: motivul, de aratat o data. */
  why?: string
  status?: string
  answered?: number
  queue_done?: boolean
}

export const auditIndexQuery = queryOptions({
  queryKey: ['settings', 'audit', 'index'],
  queryFn: () => apiGet<AuditIndex>('/settings/audit'),
})

export function auditTypeQuery(id: number) {
  return queryOptions({
    queryKey: ['settings', 'audit', 'type', id],
    queryFn: () => apiGet<AuditTypePage>(`/settings/audit/type/${id}`),
  })
}

/** `Audit::next`: raportul sau solutiile pe o fotografie incheiata. */
export function auditNext(what: 'report' | 'solutions', runId: number): Promise<AuditQueueAnswer> {
  return apiPost<AuditQueueAnswer>(`/settings/audit/next/${what}/${runId}`, {})
}

/* ---------------------------------------------------------------------------
 * Rularea (`AuditRunController`): tinta, numaratoarea, pornirea, urmarirea.
 * ------------------------------------------------------------------------- */

/** Ce trimite panoul de tinta - aceleasi campuri ca `scopeData()` din audit.js. */
export interface AuditScopeFields {
  depth: string
  devices: string
  sides: string
  classes: string
  offices: string
  types: string
}

export interface AuditScoped {
  ok: boolean
  total: number
  sides: Record<string, number>
  label: string
}

export function auditScoped(scope: AuditScopeFields): Promise<AuditScoped> {
  return apiPost<AuditScoped>('/settings/audit/scoped', { ...scope })
}

export function auditMachines(q: string): Promise<{ ok: boolean; machines: { ID: number; name: string }[] }> {
  return apiPost('/settings/audit/machines', { q })
}

export function auditRun(typeId: number, area: string, scope: AuditScopeFields): Promise<AuditQueueAnswer> {
  return apiPost<AuditQueueAnswer>('/settings/audit/run', { type_id: typeId, area, ...scope })
}

export function auditStop(runId: number): Promise<{ ok: boolean; error?: string }> {
  return apiPost(`/settings/audit/stop/${runId}`, {})
}

export function auditStatus(runId: number): Promise<AuditStatus> {
  return apiGet<AuditStatus>(`/settings/audit/status/${runId}`)
}
